import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import FormBuilder from "../../../../../../shared/components/form-builder/form-builder";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../shared/components/inputfields/form-elements";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { IInputTcDetails } from "../../../../tc-models/tc-req-dto/input-tc-product.req_dto";
import { Option } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { buyerSellerDetailsService } from "../../../../tc-service/buyer-seller-details.service";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";
import _ from "lodash";

interface InputTcDetailsProps {
  formRef: any;
  inputData: IInputTcDetails;
  ocsProcessor: any;
  setInputData: React.Dispatch<React.SetStateAction<IInputTcDetails>>;
  onTcIdSelection?: (tcId: string) => void;
  onAtcIdSelection?: (atcId: string) => void;
  isTC?: boolean | null;
}

const InputTcDetails: React.FC<InputTcDetailsProps> = ({
  formRef,
  inputData,
  ocsProcessor,
  setInputData,
  onTcIdSelection,
  onAtcIdSelection,
  isTC,
}) => {
  const { TE_ID, formtype } = useParams<string>();
  const [product, setProduct] = useState<Option[]>([]);
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const { executeGet } = useApi();
  const [cbLicensingCodes, setLicensingCodes] = useState<Option[]>([]);
  const [selectedCBLicensingCode, setSelectedCBLicensingCode] = useState<string>("");
  const hasCreateTc: boolean = formtype === "create-tc";
  const [getTcUSDAComplianceFlag, setTcUSDAComplianceFlag] =
    useState<boolean>(false);
  const [isTELabelEnabled, setTELabelEnabled] = useState(
    !inputData?.inputTcSellerTeId || inputData?.inputTcSellerTeId === ""
  );
  const [inputTCFlag, setInputTCFlag] = useState<string>("N");
  const { isRestrictPopUpSave, setRestrictPopUpSave } =
    useTransactionCertificateStore();
  const { notify } = useNotification();

  // Sync state with inputData changes
  useEffect(() => {
    if (inputData.inputTCFlag !== undefined) {
      setInputTCFlag(inputData.inputTCFlag);
    }
  }, [inputData.inputTCFlag]);

  // Fetch seller details when selectedInputTcId changes
  const fetchSellerDetailsByTcId = async (tcId: string) => {
    // Don't call TC-related APIs if ATC is selected
    if (isTC === false) {
      return;
    }
    
    try {
      const response: any = await executeGet(TCAppConfig.GET_TC_BUYERSSELLERS_DETAILS, {
        tcId: tcId
      });
      
      if (response.data && response.data.sellerDetails) {
        const sellerDetails = response.data.sellerDetails;
        
        // Update the 3 fields directly
        formRef.current.setFieldValue("inputTcSellerLicenseNo", sellerDetails.sellerLicenseNo || "");
        formRef.current.setFieldValue("inputTcSellerScNo", sellerDetails.sellerScNo?.[0] || "");
        formRef.current.setFieldValue("inputTcSellerTeId", sellerDetails.sellerTeId || "");
      }
    } catch (err) {
      console.error("Failed to fetch seller details:", err);
      notify("Failed to fetch seller details", "error");
    }
  };

  // Fetch seller details when selectedInputAtcId changes
  const fetchSellerDetailsByAtcId = async (atcId: string) => {
    // Don't call ATC-related APIs if TC is selected
    if (isTC === true) {
      return;
    }
    
    try {
      // Use the ATC details API endpoint instead of the TC buyer/seller details
      const response: any = await executeGet(
        "https://apim-te-sms-etrkit-eastus-shared-01.azure-api.net/v1/ml/get-atc-details",
        { atcId: atcId }
      );
      
      if (response.data) {
        const atcData = response.data;
        
        // Update the seller fields directly using setFieldValue
        formRef.current.setFieldValue("inputTcSellerLicenseNo", atcData.sellerName || "");
        formRef.current.setFieldValue("inputTcSellerScNo", atcData.buyerScNo || "");
        formRef.current.setFieldValue("inputTcSellerTeId", atcData.sellerTeid || "");
      }
    } catch (err) {
      console.error("Failed to fetch ATC seller details:", err);
      notify("Failed to fetch ATC seller details", "error");
    }
  };

  const fetchStandardDetails = async () => {
    const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS, {
      tcId: TE_ID,
    });
    if (response?.data) {
      const isOCSSelected = response?.data?.tcStandard.includes("OCS")
        ? true
        : false;
      setTcUSDAComplianceFlag(isOCSSelected);
    }
  };
  const fetchProductDetails = async () => {
    
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_PRODUCT_RAWMATERIAL_INFO_LIST,
        { tcId: TE_ID }
      );

      // Save the full response data for later use
      const fullResponseData = response?.data?.response || [];
      setProductDetails(fullResponseData);

      // Map over the array in the "response" key to extract the tcProductNo property
      const result: any[] =
        fullResponseData.map((detail: { tcProductNo?: string }) => ({
          value: detail.tcProductNo ?? "",
          label: detail.tcProductNo ?? "",
        })) || [];

      const uniqueData = result.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value)
      );
      setProduct(uniqueData);
    } catch (err) {
      console.error("Failed to fetch Base Details:", err);
    }
  };

  const getCBLicensingCodes = async () => {
    const result: any = await dtrackItService.getCBLicensingCodes();
    // Utills.cl(`result getCBLicensingCodes ${JSON.stringify(result)}`)
    const cbLicensing = result.data.map((t: any) => ({
      value: t.code_1,
      label: t.code_1,
    }));
    // Utills.cl(`cbLicensing ${JSON.stringify(cbLicensing)}`)
    setLicensingCodes(cbLicensing); // Set hardcoded value
  };
  useEffect(() => {
    inputData.inputTcNo = ocsProcessor.length > 0 ? "na" : inputData.inputTcNo;
    // //alert(`input-tc-details ${JSON.stringify(inputData)}`)
    getCBLicensingCodes();
    if (TE_ID) {
      fetchProductDetails();
      fetchStandardDetails();
    }
  }, []);

  // Sync form values when inputData changes (but don't reset the form)
  useEffect(() => {
    if (formRef.current) {
      // Only update fields that should be synced, not the TC/ATC fields which are managed by user selection
      if (inputData.InputCBLicensingCode !== undefined) {
        formRef.current.setFieldValue("InputCBLicensingCode", inputData.InputCBLicensingCode);
      }
      if (inputData.inputTCFlag !== undefined) {
        formRef.current.setFieldValue("inputTCFlag", inputData.inputTCFlag);
      }
      if (inputData.tcAssociatedProductNo !== undefined) {
        formRef.current.setFieldValue("tcAssociatedProductNo", inputData.tcAssociatedProductNo);
      }
      if (inputData.inputTcSellerLicenseNo !== undefined) {
        formRef.current.setFieldValue("inputTcSellerLicenseNo", inputData.inputTcSellerLicenseNo);
      }
      if (inputData.inputTcSellerScNo !== undefined) {
        formRef.current.setFieldValue("inputTcSellerScNo", inputData.inputTcSellerScNo);
      }
      if (inputData.inputTcSellerTeId !== undefined) {
        formRef.current.setFieldValue("inputTcSellerTeId", inputData.inputTcSellerTeId);
      }
      if (inputData.inputTcUsdaCompliantFlag !== undefined) {
        formRef.current.setFieldValue("inputTcUsdaCompliantFlag", inputData.inputTcUsdaCompliantFlag);
      }
    }
  }, [
    inputData.InputCBLicensingCode,
    inputData.inputTCFlag,
    inputData.tcAssociatedProductNo,
    inputData.inputTcSellerLicenseNo,
    inputData.inputTcSellerScNo,
    inputData.inputTcSellerTeId,
    inputData.inputTcUsdaCompliantFlag
  ]);

  // Initialize selectedCBLicensingCode when inputData changes
  useEffect(() => {
    if (inputData?.InputCBLicensingCode) {
      setSelectedCBLicensingCode(inputData.InputCBLicensingCode);
    }
  }, [inputData?.InputCBLicensingCode]);

  // Watch for selectedInputTcId changes and fetch seller details
  useEffect(() => {
    const selectedTcId = (inputData as any)?.selectedInputTcId;
    if (selectedTcId && selectedTcId !== "") {
      fetchSellerDetailsByTcId(selectedTcId);
    }
  }, [(inputData as any)?.selectedInputTcId]);


  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      // Fetch TE Details using the getTEDetails function
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;

      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error; // Re-throw error to handle it in the calling code
    }
  };

  const getInputTCNoForSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        tcNo: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode
      };
      const response: any = await executeGet(TCAppConfig.GET_TC_NO_INPUT_REF, queryParams);
      const data = response.data?.length === 0 ? [] : response.data.map((item: any) => ({
        label: item.tcNo,
        value: item.tcNo,
        tcId: item.tcId,
        tcVersionNo: item.tcVersionNo
      }));
      return data;
    } catch (e) {
      console.error(`Error in Fetching Input TC No ${e}`);
      return [];
    }
  };

  const getInputATCNoForSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        atcNo: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
        atcCbLicensingCode: cbLicensingCode
      };
      const response: any = await executeGet(TCAppConfig.GET_ATC_NO, queryParams);
      const data = response.data?.no_of_records === 0 ? [] : response.data.response.map((item: any) => ({
        label: item.atcNo,
        value: item.atcNo,
        tcId: item.atcId,
        tcVersionNo: item.atcVersionNo
      }));
      return data;
    } catch (e) {
      console.error(`Error in Fetching Input ATC No ${e}`);
      return [];
    }
  };
  const handleSellerTEIdValueChange = (value: any) => {
    if (value == null) {
      formRef.current.setFieldValue("inputTcSellerTeId", "");
      setTELabelEnabled(true);
    } else {
      formRef.current.setFieldValue("inputTcSellerTeId", value.TE_ID);
      setTELabelEnabled(false);
    }
  };
  // Create stable initial values to prevent form reset
  const stableInitialValues = React.useMemo(() => ({
    InputCBLicensingCode: inputData.InputCBLicensingCode || "",
    inputTCFlag: inputData.inputTCFlag || "N",
    inputTcNo: inputData.inputTcNo || "",
    inputAtcNo: inputData.inputAtcNo || "",
    tcAssociatedProductNo: inputData.tcAssociatedProductNo || "",
    inputTcSellerLicenseNo: inputData.inputTcSellerLicenseNo || "",
    inputTcSellerScNo: inputData.inputTcSellerScNo || "",
    inputTcSellerTeId: inputData.inputTcSellerTeId || "",
    inputTcUsdaCompliantFlag: inputData.inputTcUsdaCompliantFlag || "",
  }), [
    // Only include fields that should trigger a reset, exclude dynamic fields
    inputData.InputCBLicensingCode,
    inputData.inputTCFlag,
    // Don't include inputTcNo and inputAtcNo in dependencies to prevent reset on selection
    inputData.tcAssociatedProductNo,
    inputData.inputTcSellerLicenseNo,
    inputData.inputTcSellerScNo,
    inputData.inputTcSellerTeId,
    inputData.inputTcUsdaCompliantFlag
  ]);

  const formConfig = {
    pageName: "",
    initialValues: stableInitialValues,
    validationSchema: Yup.object().shape({
      InputCBLicensingCode: Yup.string(),
      inputTCFlag: Yup.string().oneOf(["Y", "N"], "Please select Yes or No").required("Please select Yes or No"),
      inputTcNo: Yup.string()
        .max(100, "Maximum 100 Character is allowed")
        .test('tc-or-atc-required', 'Either TC No or ATC No is required', function(value) {
          const { inputAtcNo, inputTCFlag } = this.parent;
          // If "Not Applicable" is selected, skip validation
          if (inputTCFlag === "Y") {
            return true;
          }
          return !!(value || inputAtcNo);
        }),
      inputAtcNo: Yup.string()
        .max(100, "Maximum 100 Character is allowed")
        .test('tc-or-atc-required', 'Either TC No or ATC No is required', function(value) {
          const { inputTcNo, inputTCFlag } = this.parent;
          // If "Not Applicable" is selected, skip validation
          if (inputTCFlag === "Y") {
            return true;
          }
          return !!(value || inputTcNo);
        }),
      tcAssociatedProductNo: Yup.string().required(
        "Associated Product No. is required"
      ),
      inputTcSellerLicenseNo: Yup.string().nullable(),
      inputTcSellerScNo: Yup.string().nullable(),
      inputTcSellerTeId: Yup.string().nullable(),
      inputTcUsdaCompliantFlag: Yup.string().nullable(),
    }),
    sections: [
      {
        title: "Input Details",
        fields: [
          {
            label: "Associated Product No.",
            name: "tcAssociatedProductNo",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: "",
            props: {
              options: product,
              placeholder: "Select Product No.",
              variant: "outlined",
              fullWidth: true,
              onChange: (e: any) => {
                formRef.current.setFieldValue("tcAssociatedProductNo", e.target.value);
              },
            },
          },
          {
            label: "Input CB Licensing Code",
            name: "InputCBLicensingCode",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              enableSearch: true,
              options: cbLicensingCodes,
              placeholder: "Select CB Name",
              onChange: (e: any) => {
                formRef.current.setFieldValue("InputCBLicensingCode", e.target.value);
                setSelectedCBLicensingCode(e.target.value);
                // Clear Input TC No field when CB Licensing Code changes
                formRef.current.setFieldValue("inputTcNo", "");
              },
            },
          },
          {
            label: "Is TC/ATC Not Applicable?",
            name: "inputTCFlag",
            type: "radio",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: true,
            
            props: {
              options: [
                { label: "No", value: "N" },
                { label: "Yes", value: "Y" }
              ],
            },
            events: {
              onChange: (selectedValue: any, values: any, setFieldValue: any) => {
                setInputTCFlag(selectedValue);
                
                if (selectedValue === "Y") {
                  // Set na values and clear parent state
                  setFieldValue("inputTcNo", "na");
                  setFieldValue("inputAtcNo", "na");
                  formRef.current.setFieldValue("inputTcNoData", null);
                  formRef.current.setFieldValue("inputAtcNoData", null);
                  
                  // Clear parent state
                  if (onTcIdSelection) {
                    onTcIdSelection("");
                  }
                } else {
                  // Clear fields when switching back to No
                  setFieldValue("inputTcNo", "");
                  setFieldValue("inputAtcNo", "");
                }
              },
            },
          },
          {
            label: "Input TC No.",
            name: "inputTcNo",
            type: inputTCFlag === "Y" ? "text" : "search",
            disabled: ocsProcessor.length > 0 ? true : (!selectedCBLicensingCode && inputTCFlag !== "Y") || inputTCFlag === "Y",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: inputTCFlag !== "Y",
            props: inputTCFlag === "Y" ? {
              placeholder: "na",
              fullWidth: true,
            } : {
              placeholder: "Search Input TC No",
              displayKey: "label",
              onSearch: (query: string, offset: number) => {
                return getInputTCNoForSearch(query, offset, "query", selectedCBLicensingCode);
              },
              fullWidth: true,
            },
            events: {
              onChange: (selectedObject: any, values: any, setFieldValue: any) => {
                // Handle the selection properly
                if (selectedObject && typeof selectedObject === 'object' && selectedObject.tcId) {
                  // Set the string value for display
                  const stringValue = selectedObject.label || selectedObject.value || '';
                  setFieldValue("inputTcNo", stringValue);
                  
                  // Store the full object separately for later use
                  setFieldValue("inputTcNoData", selectedObject);
                  
                  // Clear and disable ATC field when TC is selected
                  setFieldValue("inputAtcNo", "");
                  setFieldValue("inputAtcNoData", null);
                  
                  // Call parent callback to pass tcId
                  if (onTcIdSelection) {
                    onTcIdSelection(selectedObject.tcId);
                  }
                  
                  // Fetch seller details for TC
                  fetchSellerDetailsByTcId(selectedObject.tcId);
                } else if (selectedObject === null) {
                  // Handle clear action
                  setFieldValue("inputTcNo", "");
                  setFieldValue("inputTcNoData", null);
                  
                  // Clear parent state
                  if (onTcIdSelection) {
                    onTcIdSelection("");
                  }
                }
              },
            },
          },
           {
             label: "Input ATC No.",
             name: "inputAtcNo",
             type: inputTCFlag === "Y" ? "text" : "search",
             disabled: ocsProcessor.length > 0 ? true : (!selectedCBLicensingCode && inputTCFlag !== "Y") || inputTCFlag === "Y",
             gridSize: { xs: 12, md: 4, lg: 4 },
             required: inputTCFlag !== "Y",
             props: inputTCFlag === "Y" ? {
               placeholder: "na",
               fullWidth: true,
             } : {
               placeholder: "Search Input ATC No",
               displayKey: "label",
               onSearch: (query: string, offset: number) => {
                 return getInputATCNoForSearch(query, offset, "query", selectedCBLicensingCode);
               },
               fullWidth: true,
             },
             events: {
               onChange: (selectedObject: any, values: any, setFieldValue: any) => {
                 // Handle the selection properly
                 if (selectedObject && typeof selectedObject === 'object' && selectedObject.tcId) {
                   // Set the string value for display
                   const stringValue = selectedObject.label || selectedObject.value || '';
                   setFieldValue("inputAtcNo", stringValue);
                   
                   // Store the full object separately for later use
                   setFieldValue("inputAtcNoData", selectedObject);
                   
                   // Clear and disable TC field when ATC is selected
                   setFieldValue("inputTcNo", "");
                   setFieldValue("inputTcNoData", null);
                   
                   // Call parent callback to pass atcId
                   if (onAtcIdSelection) {
                     onAtcIdSelection(selectedObject.tcId);
                   }
                   
                   // Fetch seller details for ATC
                   fetchSellerDetailsByAtcId(selectedObject.tcId);
                 } else if (selectedObject === null) {
                   // Handle clear action
                   setFieldValue("inputAtcNo", "");
                   setFieldValue("inputAtcNoData", null);
                   
                   // Clear parent state
                   if (onAtcIdSelection) {
                     onAtcIdSelection("");
                   }
                 }
               },
             },
           },
          {
            label: "Seller License No.", //'Input Seller License No..', renamed to match tbl hdr
            name: "inputTcSellerLicenseNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Enter Seller License No.",
            },
          },
          {
            label: "Seller SC No.", //renamed to match table hdr was Input Seller SC No.
            name: "inputTcSellerScNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Enter Seller SC No.",
            },
          },
          {
            label: "Input TC Seller TE-ID", //renamed to match table header
            name: "inputTcSellerTeId",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Search Seller TE-ID",
              variant: "outlined",
              param: "inputTcSellerTeId",
              restrictInputFormat: isTELabelEnabled,
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) => {
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "USDA Compliance", //renamed to match table header
            name: "inputTcUsdaCompliantFlag",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            visible: (values: any) => getTcUSDAComplianceFlag,
            required: false,
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
              placeholder: "Select Compliance",
            },
          },
          // {
          //   label: 'Input Certified Weight Used (e.g format: RM0064-1000)',
          //   name: 'inputTcCertifiedWeightUsed',
          //   type: 'chips',
          //   gridSize: { xs: 12, md: 12, lg: 12 },
          //   required: false,
          //   props: {
          //     placeholder: 'Enter Certified Weight',
          //     helpText: "once value is entered please click Enter button to add"
          //   },
          // },
        ],
      },
    ],
         handleSubmit: (values: any) => {
       // Process the form values - merge with existing inputData to preserve user input
       const processedValues = { ...inputData, ...values };
       
       // Handle TC data if present
       if (values.inputTcNoData && values.inputTcNoData.tcId) {
         processedValues.inputTcNo = values.inputTcNo; // This is now the string value
         processedValues.selectedTcId = values.inputTcNoData.tcId;
         processedValues.selectedTcVersionNo = values.inputTcNoData.tcVersionNo;
       }
       
       // Handle ATC data if present
       if (values.inputAtcNoData && values.inputAtcNoData.tcId) {
         processedValues.inputAtcNo = values.inputAtcNo; // This is now the string value
         processedValues.selectedTcId = values.inputAtcNoData.tcId;
         processedValues.selectedTcVersionNo = values.inputAtcNoData.tcVersionNo;
       }
       
       setInputData(processedValues);
     },
  };









  return (
    <>
      <FormBuilder
        formConfig={formConfig}
        formRef={formRef}
      />
    </>
  );
};

export default InputTcDetails;
